import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { createSelector } from "reselect";

import { protectedResources } from "../../authConfig";
import { setAuthInteractionRequired } from "../../msalAuthBridge";
import { permissionEntities, permissionTypes } from "../../services/access-control";
import { selectAllLocationObjectsWithUsers, selectRoles } from "./roles-slice";
import { selectUsers } from "./users-slice";

// define initial state
const initialState = {
  account: null,
  token: false,
  userProfile: {
    userId: null,
    userIdNotFound: false,
  },
};

// create slice
export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setAccount: (state, action) => {
      state.account = action.payload;
    },
    setToken: (state, action) => {
      state.token = action.payload;
    },
    signOut: (state) => {
      state.token = null;
      state.account = null;
    },
    setLoggedInUserId: (state, action) => {
      state.userProfile.userId = action.payload;
      axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        "app-user-id": action.payload,
      };
    },
    setUserIdNotFound: (state, action) => {
      state.userProfile.userIdNotFound = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setAccount,
  setToken,
  signOut,
  setLoggedInUserId,
  setUserIdNotFound,
} = authSlice.actions;

// Add this at module scope (top of auth-slice.js/ts, outside any function)
let tokenRequestInFlight = null;

export const setTokenAsync = (instance) => async (dispatch, getState) => {
  const { account } = getState().auth;
  if (!account) return;

  // ✅ Deduplicate: if React dev double-invokes effects (or multiple callers),
  // reuse the same acquireTokenSilent request instead of starting a new iframe flow.
  if (tokenRequestInFlight) {
    return tokenRequestInFlight;
  }

  const tokenRequest = {
    account,
    scopes: protectedResources.appServiceApi.scopes,
  };

  tokenRequestInFlight = (async () => {
    try {
      const response = await instance.acquireTokenSilent(tokenRequest);

      axios.defaults.headers.common = {
        ...axios.defaults.headers.common,
        Authorization: `Bearer ${response.accessToken}`,
      };

      dispatch(setToken(true));
      return response;
    } catch (e) {
      // ✅ Standard MSAL case: silent can't complete, user interaction required
      if (e instanceof InteractionRequiredAuthError) {
        setAuthInteractionRequired({
          reason: "interaction_required",
          scopes: protectedResources.appServiceApi.scopes,
        });
        return;
      }

      // ✅ MSAL 5+ guardrails: silent iframe reload blocked or redirect bridge timed out
      // These often happen when the thunk is called twice or the iframe flow is blocked.
      const errorCode = e?.errorCode || e?.code;
      const subError = e?.subError;

      if (
        errorCode === "block_iframe_reload" ||
        errorCode === "timed_out" ||
        subError === "redirect_bridge_timeout"
      ) {
        setAuthInteractionRequired({
          reason: errorCode || subError || "interaction_required",
          scopes: protectedResources.appServiceApi.scopes,
        });
        return;
      }

      throw e;
    } finally {
      tokenRequestInFlight = null;
    }
  })();

  return tokenRequestInFlight;
};


// ----------------------
// Selectors (RESTORED)
// ----------------------

export const selectAccount = (state) => state.auth.account;
export const selectToken = (state) => state.auth.token;
export const selectCurrentUserProfileId = (state) => state.auth?.userProfile?.userId;

export const selectCurrentUserId = (state) => state.auth.account?.localAccountId;

export const selectLoggedInUserInfo = createSelector(
  [selectCurrentUserId, selectUsers],
  (userId, users) => {
    if (!userId || Object.keys(users).length === 0) {
      return {};
    }
    return Object.values(users).find((user) => user.activeDirectoryId === userId) ?? {};
  }
);

export const selectLoggedInUserLocations = createSelector(
  [selectAllLocationObjectsWithUsers, selectLoggedInUserInfo],
  (locations, userInfo) => {
    const userLocations = Object.values(locations).filter(
      (location) => location.userIds && location.userIds.includes(userInfo.userId)
    );

    return userLocations.sort().reverse();
  }
);

export const selectAllRepairLocations = createSelector(
  [selectLoggedInUserLocations],
  (locations) => (locations ?? []).filter((location) => location.IsRepairShop === "True")
);

export const selectAllActiveUserRepairLocationObjects = createSelector(
  [selectAllRepairLocations],
  (locations) =>
    locations.reduce((current, l) => ({ ...current, [l.LocationID]: l }), {})
);

export const selectLoggedInUserActiveLocation = createSelector(
  [selectAllLocationObjectsWithUsers, selectLoggedInUserInfo],
  (locations, userInfo) => {
    return locations[userInfo.locationPreference] ?? {};
  }
);

export const selectIsAdmin = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const adminRole = Object.values(roles).find((role) => role.name === "Application Admin");
    const found = adminRole?.users?.find((user) => user?.userId === userId);
    return !!found;
  }
);

export const selectIsDev = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const devRole = Object.values(roles).find((role) => role.name === "Developer");
    const found = devRole?.users?.find((user) => user?.userId === userId);
    return !!found;
  }
);

export const selectIsAssetAdmin = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const adminRole = Object.values(roles).find((role) => role.name === "Application Admin");
    const maintManagerRole = Object.values(roles).find(
      (role) => role.name === "Maintenance Manager"
    );

    const found =
      adminRole?.users?.find((user) => user?.userId === userId) ||
      maintManagerRole?.users?.find((user) => user?.userId === userId);

    return !!found;
  }
);

export const selectIsAssetReadOnly = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const role = Object.values(roles).find((item) => item.name === "Asset Read Only");
    return !!role?.users?.find((user) => user?.userId === userId);
  }
);

export const selectIsProductPackageAdmin = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const prodPackageRole = Object.values(roles).find((role) => role.name === 'Packaged Prod Admin');
    const found = prodPackageRole?.users?.find((user) => { return user?.userId === userId; });
    return !!found;
  },
);

export const selectIsMaintenanceManager = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const maintManagerRole = Object.values(roles).find(
      (role) => role.name === "Maintenance Manager"
    );
    const found = maintManagerRole?.users?.find((user) => user?.userId === userId);
    return !!found;
  }
);

// NOTE: keep the misspelling because your imports expect it
export const selectCurentUserRoles = createSelector(
  [selectRoles, selectCurrentUserProfileId],
  (roles, userId) => {
    const rolesList = Object.values(roles);
    const currRoles = [];
    rolesList.forEach((role) => {
      const inRole = role.users?.find((user) => user?.userId === userId);
      if (inRole) currRoles.push(role);
    });
    return currRoles;
  }
);

export const selectUserAccountAndProfileIsLoaded = createSelector(
  [selectAccount, selectUsers],
  (userId, users) => {
    if (!userId || Object.keys(users).length === 0) {
      return {};
    }
    return userId in users ? users[userId] : {};
  }
);

export const selectLoggedInUserPreferences = createSelector(
  [selectLoggedInUserInfo],
  (userInfo) => ({
    datePreference: userInfo?.datePreference ?? 0,
    measurementPreference: userInfo?.measurementPreference ?? 0,
    numberPreference: userInfo?.numberPreference ?? 0,
  })
);

export const selectCurrentUserRoleIds = createSelector(
  [selectCurrentUserId, selectUsers],
  (userId, users) => {
    if (!userId || Object.keys(users).length === 0) {
      return [];
    }
    return users[userId].roles.map((r) => r.roleId);
  }
);

export const selectCurrentUserPermissions = createSelector(
  [selectCurrentUserRoleIds, selectRoles],
  (roleIds, roles) => {
    if (Object.keys(roles).length === 0 || roles.length === 0) {
      return [];
    }
    return roleIds
      .map((roleId) => roles[roleId])
      .map((r) => r.permissions)
      .flat()
      .map((p) => ({
        ...p,
        entity: permissionEntities[p.entity],
        type: permissionTypes[p.type],
      }));
  }
);

export default authSlice.reducer;
